import { useCallback, useEffect, useState } from "react";

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

export type PwaPlatform = "ios" | "android" | "desktop";
export type PwaInstallResult = "accepted" | "dismissed" | "unavailable" | "installed";

export function installedDisplayMode(): boolean {
  if (typeof window === "undefined") return false;
  const navigatorWithStandalone = navigator as Navigator & { standalone?: boolean };
  return window.matchMedia("(display-mode: standalone)").matches || navigatorWithStandalone.standalone === true;
}

export function platformName(): PwaPlatform {
  if (typeof window === "undefined") return "desktop";
  const userAgent = navigator.userAgent.toLocaleLowerCase("en-US");
  if (/iphone|ipad|ipod/.test(userAgent)) return "ios";
  if (/android/.test(userAgent)) return "android";
  return "desktop";
}

export function usePwaInstall() {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(installedDisplayMode);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    const capturePrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };
    const markInstalled = () => {
      setInstalled(true);
      setInstallPrompt(null);
      setNotice("PuntoPY quedó instalado en este dispositivo.");
    };
    window.addEventListener("beforeinstallprompt", capturePrompt);
    window.addEventListener("appinstalled", markInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", capturePrompt);
      window.removeEventListener("appinstalled", markInstalled);
    };
  }, []);

  const requestInstall = useCallback(async (): Promise<PwaInstallResult> => {
    setNotice(null);
    if (installed) {
      setNotice("PuntoPY ya está instalado. Buscá el icono en la pantalla de inicio o en tus aplicaciones.");
      return "installed";
    }
    if (installPrompt === null) return "unavailable";
    try {
      await installPrompt.prompt();
      const choice = await installPrompt.userChoice;
      setInstallPrompt(null);
      if (choice.outcome === "accepted") {
        setInstalled(true);
        setNotice("Instalación aceptada. El dispositivo terminará de agregar PuntoPY.");
        return "accepted";
      }
      setNotice("La instalación fue cancelada. Podés intentarlo nuevamente desde el menú del navegador.");
      return "dismissed";
    } catch {
      setInstallPrompt(null);
      setNotice("El navegador no pudo abrir la instalación. Usá la opción del menú para agregar PuntoPY a tu dispositivo.");
      return "unavailable";
    }
  }, [installed, installPrompt]);

  return {
    canInstall: installPrompt !== null,
    installed,
    notice,
    requestInstall
  };
}
